// @ts-nocheck
// ============================================================
// TWIN DESIGN SYSTEM — Fase 10.20
// Componentes visuais do Digital Twin
// ============================================================
import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { SEVERITY, SURFACE, TEXT } from './tokens'
import type { ObraZone, Equipment, SpatialRisk, OperationalRecommendation, SimulationResult } from '@/types/twin'

// ─── Type guard for spatial bounds ───────────────────────
function isBounds(v: unknown): v is { x: number; y: number; w: number; h: number } {
  if (typeof v !== 'object' || v === null) return false
  const b = v as Record<string, unknown>
  return typeof b['x'] === 'number' && typeof b['y'] === 'number' &&
         typeof b['w'] === 'number' && typeof b['h'] === 'number'
}

// ─── Risk level helpers ──────────────────────────────────
const RISK_COLOR: Record<string, string> = {
  low:       '#10b981',
  moderate:  '#f59e0b',
  high:      '#f97316',
  critical:  '#ef4444',
  emergency: '#dc2626',
}

const RISK_BG: Record<string, string> = {
  low:       'rgba(16,185,129,0.12)',
  moderate:  'rgba(245,158,11,0.12)',
  high:      'rgba(249,115,22,0.12)',
  critical:  'rgba(239,68,68,0.15)',
  emergency: 'rgba(220,38,38,0.20)',
}

// ─── TwinZone ────────────────────────────────────────────
interface TwinZoneProps {
  zone:       ObraZone
  selected?:  boolean
  onClick?:   () => void
  compact?:   boolean
}

export function TwinZone({ zone, selected, onClick, compact }: TwinZoneProps) {
  const color = RISK_COLOR[zone.risk_level] ?? RISK_COLOR.low
  const bg    = RISK_BG[zone.risk_level]    ?? RISK_BG.low

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      style={{
        background:    selected ? bg : SURFACE.card,
        border:        `1.5px solid ${selected ? color : 'rgba(255,255,255,0.08)'}`,
        borderRadius:  8,
        padding:       compact ? '8px 12px' : '12px 16px',
        cursor:        onClick ? 'pointer' : 'default',
        display:       'flex',
        alignItems:    'center',
        gap:           10,
        transition:    'all 0.2s',
        position:      'relative',
        overflow:      'hidden',
      }}
    >
      {/* Risk accent bar */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: 3, background: color, borderRadius: '8px 0 0 8px',
      }} />

      <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 13, fontWeight: 600, color: TEXT.primary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {zone.nome}
          </span>
          <RiskBadge level={zone.risk_level} small />
        </div>
        {!compact && zone.tipo && (
          <span style={{ fontSize: 11, color: TEXT.muted }}>
            {zone.tipo.replace('_', ' ')}
          </span>
        )}
      </div>

      {zone.risk_level === 'critical' || zone.risk_level === 'emergency' ? (
        <motion.div
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }}
        />
      ) : (
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      )}
    </motion.div>
  )
}

// ─── RiskBadge ───────────────────────────────────────────
interface RiskBadgeProps { level: string; small?: boolean }

export function RiskBadge({ level, small }: RiskBadgeProps) {
  const color = RISK_COLOR[level] ?? RISK_COLOR.low
  const label: Record<string, string> = {
    low: 'Baixo', moderate: 'Moderado', high: 'Alto',
    critical: 'Crítico', emergency: 'EMERGÊNCIA',
  }
  return (
    <span style={{
      fontSize:     small ? 9 : 11,
      fontWeight:   700,
      color,
      background:   RISK_BG[level] ?? RISK_BG.low,
      border:       `1px solid ${color}`,
      borderRadius: 4,
      padding:      small ? '1px 4px' : '2px 6px',
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
    }}>
      {label[level] ?? level}
    </span>
  )
}

// ─── EquipmentStatus ─────────────────────────────────────
interface EquipmentStatusProps { equipment: Equipment; compact?: boolean }

const EQUIP_STATUS_COLOR: Record<string, string> = {
  ok:        '#10b981',
  degraded:  '#f59e0b',
  critical:  '#ef4444',
  offline:   '#6b7280',
  maintenance: '#8b5cf6',
}

export function EquipmentStatus({ equipment, compact }: EquipmentStatusProps) {
  const color  = EQUIP_STATUS_COLOR[equipment.status] ?? '#6b7280'
  const isDown = equipment.status === 'offline' || equipment.status === 'critical'

  return (
    <div style={{
      display:    'flex',
      alignItems: 'center',
      gap:        8,
      padding:    compact ? '6px 10px' : '10px 14px',
      background: SURFACE.card,
      border:     `1px solid ${isDown ? color : 'rgba(255,255,255,0.07)'}`,
      borderRadius: 8,
    }}>
      {isDown ? (
        <motion.div
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ repeat: Infinity, duration: 1.2 }}
          style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }}
        />
      ) : (
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: TEXT.primary }}>{equipment.nome}</div>
        {!compact && (
          <div style={{ fontSize: 11, color: TEXT.muted }}>
            {equipment.tipo} · {equipment.status}
          </div>
        )}
      </div>
      {!compact && equipment.battery_pct !== undefined && (
        <div style={{ fontSize: 11, color: equipment.battery_pct < 20 ? '#f59e0b' : TEXT.muted }}>
          🔋 {equipment.battery_pct}%
        </div>
      )}
    </div>
  )
}

// ─── SpatialRiskCard ─────────────────────────────────────
interface SpatialRiskCardProps { risk: SpatialRisk }

export function SpatialRiskCard({ risk }: SpatialRiskCardProps) {
  const color = SEVERITY[risk.severity as keyof typeof SEVERITY] ?? SEVERITY.warning

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        display:      'flex',
        gap:          10,
        padding:      '10px 14px',
        background:   SURFACE.card,
        border:       `1px solid ${color}22`,
        borderLeft:   `3px solid ${color}`,
        borderRadius: 8,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: TEXT.primary }}>{risk.title}</div>
        {risk.description && (
          <div style={{ fontSize: 11, color: TEXT.muted, marginTop: 2 }}>{risk.description}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: TEXT.muted }}>{risk.risk_type}</span>
          <ScoreBar value={risk.score} />
        </div>
      </div>
    </motion.div>
  )
}

function ScoreBar({ value }: { value: number }) {
  const pct   = Math.round(value * 100)
  const color = pct >= 80 ? '#ef4444' : pct >= 60 ? '#f97316' : pct >= 40 ? '#f59e0b' : '#10b981'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ width: 48, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 10, color, fontWeight: 600 }}>{pct}</span>
    </div>
  )
}

// ─── AutonomousAlert ─────────────────────────────────────
interface AutonomousAlertProps {
  title:    string
  message:  string
  severity: 'info' | 'warning' | 'critical' | 'emergency'
  time?:    string
  onDismiss?: () => void
}

export function AutonomousAlert({ title, message, severity, time, onDismiss }: AutonomousAlertProps) {
  const color = severity === 'emergency' ? '#dc2626'
              : severity === 'critical'  ? '#ef4444'
              : severity === 'warning'   ? '#f59e0b'
              : '#3b82f6'

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 20 }}
      style={{
        padding:      '12px 16px',
        background:   `${color}14`,
        border:       `1px solid ${color}44`,
        borderLeft:   `4px solid ${color}`,
        borderRadius: 8,
        position:     'relative',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 2 }}>
            {severity === 'emergency' && '🚨 '}{title}
          </div>
          <div style={{ fontSize: 12, color: TEXT.secondary }}>{message}</div>
          {time && <div style={{ fontSize: 10, color: TEXT.muted, marginTop: 4 }}>{time}</div>}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{ background: 'none', border: 'none', color: TEXT.muted, cursor: 'pointer', fontSize: 16, padding: 0 }}
          >✕</button>
        )}
      </div>
    </motion.div>
  )
}

// ─── SimulationCard ──────────────────────────────────────
interface SimulationCardProps { result: SimulationResult }

export function SimulationCard({ result }: SimulationCardProps) {
  const risk     = result.impact_summary.risk_delta
  const color    = risk >= 40 ? '#ef4444' : risk >= 25 ? '#f97316' : '#f59e0b'
  const delay    = result.impact_summary.schedule_delay_days ?? 0
  const costImpact = result.impact_summary.cost_impact_pct  ?? 0

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{
        padding:      16,
        background:   SURFACE.card,
        border:       '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
      }}
    >
      <div style={{ fontSize: 12, color: TEXT.muted, marginBottom: 8 }}>
        {new Date(result.ran_at).toLocaleString('pt-BR')}
      </div>
      <p style={{ fontSize: 13, color: TEXT.secondary, margin: '0 0 12px' }}>
        {result.impact_summary.narrative}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
        {[
          { label: 'Risco Δ', value: `+${risk}`, color },
          { label: 'Atraso', value: `${delay}d`, color: delay > 3 ? '#ef4444' : TEXT.primary },
          { label: 'Custo Δ', value: `${costImpact}%`, color: costImpact > 5 ? '#f97316' : TEXT.primary },
        ].map(m => (
          <div key={m.label} style={{ textAlign: 'center', padding: '8px 4px', background: 'rgba(255,255,255,0.04)', borderRadius: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: m.color }}>{m.value}</div>
            <div style={{ fontSize: 10, color: TEXT.muted }}>{m.label}</div>
          </div>
        ))}
      </div>

      {result.recommendations.length > 0 && (
        <ul style={{ margin: 0, padding: '0 0 0 16px', listStyle: 'disc' }}>
          {result.recommendations.slice(0, 3).map((r, i) => (
            <li key={i} style={{ fontSize: 12, color: TEXT.secondary, marginBottom: 2 }}>{r}</li>
          ))}
        </ul>
      )}
    </motion.div>
  )
}

// ─── OperationalRecommendationCard ───────────────────────
interface RecCardProps { rec: OperationalRecommendation; onDismiss?: () => void }

const PRIORITY_LABEL: Record<number, string> = { 1: 'Imediato', 2: 'Hoje', 3: 'Esta semana' }
const PRIORITY_COLOR: Record<number, string> = { 1: '#ef4444', 2: '#f59e0b', 3: '#3b82f6' }

export function RecommendationCard({ rec, onDismiss }: RecCardProps) {
  const color = PRIORITY_COLOR[rec.priority] ?? '#6b7280'

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      style={{
        padding:      '12px 14px',
        background:   SURFACE.card,
        border:       `1px solid ${color}33`,
        borderLeft:   `3px solid ${color}`,
        borderRadius: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color, background: `${color}22`, padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase' }}>
              {PRIORITY_LABEL[rec.priority] ?? `P${rec.priority}`}
            </span>
            <span style={{ fontSize: 11, color: TEXT.muted }}>{rec.category}</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: TEXT.primary, marginBottom: 3 }}>{rec.title}</div>
          <div style={{ fontSize: 12, color: TEXT.secondary }}>{rec.action}</div>
          <div style={{ fontSize: 11, color: TEXT.muted, marginTop: 4 }}>
            Impacto: {rec.impact} · Confiança: {Math.round(rec.confidence * 100)}%
          </div>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: TEXT.muted, cursor: 'pointer', fontSize: 14, flexShrink: 0 }}>✕</button>
        )}
      </div>
    </motion.div>
  )
}

// ─── OperationalLayer toggle ──────────────────────────────
interface LayerToggleProps {
  id:       string
  label:    string
  icon:     string
  active:   boolean
  color?:   string
  onToggle: () => void
}

export function LayerToggle({ id, label, icon, active, color = '#3b82f6', onToggle }: LayerToggleProps) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onToggle}
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          6,
        padding:      '6px 10px',
        background:   active ? `${color}22` : 'rgba(255,255,255,0.04)',
        border:       `1px solid ${active ? color : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 20,
        color:        active ? color : TEXT.muted,
        fontSize:     12,
        fontWeight:   active ? 700 : 400,
        cursor:       'pointer',
        transition:   'all 0.2s',
      }}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </motion.button>
  )
}

// ─── SpatialRiskOverlay (SVG) ────────────────────────────
interface SpatialRiskOverlayProps {
  zones:     ObraZone[]
  width:     number
  height:    number
  onZoneClick?: (id: string) => void
}

export function SpatialRiskOverlay({ zones, width, height, onZoneClick }: SpatialRiskOverlayProps) {
  return (
    <svg width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
      {zones.map(zone => {
        if (!zone.spatial_bounds) return null
        // FIX PROB-20: type-guard before destructuring
        const bounds = zone.spatial_bounds
        if (!isBounds(bounds)) return null
        const { x, y, w, h } = bounds
        const px = x * width, py = y * height, pw = w * width, ph = h * height
        const color = RISK_COLOR[zone.risk_level] ?? RISK_COLOR.low

        return (
          <g key={zone.id} style={{ pointerEvents: 'all', cursor: onZoneClick ? 'pointer' : 'default' }}
            onClick={() => onZoneClick?.(zone.id)}>
            <rect
              x={px} y={py} width={pw} height={ph}
              fill={`${color}20`} stroke={color} strokeWidth={1.5}
              rx={4}
            />
            <text x={px + 6} y={py + 16} fontSize={10} fill={color} fontWeight={700}>
              {zone.nome}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ─── TwinStatusBar ───────────────────────────────────────
interface TwinStatusBarProps {
  riskScore:       number
  criticalZones:   number
  criticalEquip:   number
  activeCorrelations: number
  lastUpdated:     string | null
}

export function TwinStatusBar({ riskScore, criticalZones, criticalEquip, activeCorrelations, lastUpdated }: TwinStatusBarProps) {
  const level  = riskScore >= 70 ? 'critical' : riskScore >= 45 ? 'high' : riskScore >= 20 ? 'moderate' : 'low'
  const color  = RISK_COLOR[level]

  return (
    <div style={{
      display:        'flex',
      alignItems:     'center',
      gap:            16,
      padding:        '8px 16px',
      background:     SURFACE.card,
      border:         '1px solid rgba(255,255,255,0.08)',
      borderRadius:   8,
      flexWrap:       'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <motion.div
          animate={level === 'critical' ? { opacity: [1, 0.3, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.5 }}
          style={{ width: 10, height: 10, borderRadius: '50%', background: color }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, color }}>
          Risk Score: {Math.round(riskScore)}
        </span>
      </div>
      {[
        { label: 'Zonas críticas', value: criticalZones, warn: criticalZones > 0 },
        { label: 'Equip. críticos', value: criticalEquip, warn: criticalEquip > 0 },
        { label: 'Correlações', value: activeCorrelations, warn: activeCorrelations > 0 },
      ].map(m => (
        <div key={m.label} style={{ fontSize: 12, color: m.warn ? '#f97316' : TEXT.muted }}>
          <span style={{ fontWeight: m.warn ? 700 : 400 }}>{m.value}</span> {m.label}
        </div>
      ))}
      {lastUpdated && (
        <div style={{ fontSize: 10, color: TEXT.muted, marginLeft: 'auto' }}>
          Atualizado {new Date(lastUpdated).toLocaleTimeString('pt-BR')}
        </div>
      )}
    </div>
  )
}
